<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :title="$t('resources.categories.page_categories')"
    :sub-title="$t('resources.categories.page_categories_sub_title')">
    <template #actions>
      <v-btn
        v-if="totalItems || noData || loading"
        outlined
        color="primary"
        class="left-icon"
        :to="listPage ? { name: 'page_categories_create' } : { name: 'page_categories_create', query: { domainId } }">
        <plus-icon class="mr-2 fill--primary"></plus-icon>
        {{ $t("generic.buttons.create") }}
      </v-btn>
    </template>
    <template #default>
      <delete-page-categories-modal :domain-id="domainId"></delete-page-categories-modal>
      <va-list
        ref="list"
        :resource="resourceName"
        disable-query-string
        :filter.sync="customFiltersValues"
        :filters="filters"
        disable-export
        disable-create
        :ignore-domain-filter="!!domainId"
        hide-domain-filter
        @update:selected="getBulkSelectedItems"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <empty-list
            :list-page="listPage"
            :title="$t('resources.categories.empty_list_title')"
            :subtitle="$t('resources.categories.empty_list_subtitle')"
            :route="
              listPage ? { name: 'page_categories_create' } : { name: 'page_categories_create', query: { domainId } }
            "></empty-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template v-slot:[`bulk.actions`]>
          <v-btn
            @click="editResource('page_categories', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-pencil-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
          </v-btn>
          <v-btn
            @click="duplicateResource('page_categories', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-file-multiple-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.duplicate") }}</span>
          </v-btn>
        </template>
        <template #[`filter.search`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table :fields="fields" class="va-table--selectable" @row-click="handleRowClick">
          <template #[`field.name`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <span>{{ item.name }}</span>
            </div>
          </template>
          <template #[`field.slug`]="{ item }">
            <div class="cell-ellipsis">
              <span>{{ item.slug }}</span>
            </div>
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                {
                  title: 'Edit',
                  icon: 'mdi-pencil-outline',
                  function: editResource.bind(this, 'page_categories', item.id),
                },
                {
                  title: 'Duplicate',
                  icon: 'mdi-file-multiple-outline',
                  function: duplicateResource.bind(this, 'page_categories', item.id),
                },
                {
                  title: 'Delete',
                  icon: 'mdi-trash-can-outline',
                  function: deleteResource.bind(this, item),
                },
              ]">
            </DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import DataListActions from "@/components/custom/DataListActions";
  import PlusIcon from "@/components/icons/PlusIcon";
  import EmptyList from "@/components/custom/EmptyList";
  import debounce from "lodash.debounce";
  import DeletePageCategoriesModal from "@/components/resources/DeletePageCategoriesModal";
  import RowClick from "@/mixins/rowClickMixin";

  export default {
    components: {
      ResourceListWrapper,
      DataListActions,
      PlusIcon,
      EmptyList,
      DeletePageCategoriesModal,
    },
    mixins: [RowClick],
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      activeFilterOnload: {
        type: Object,
        default: () => ({}),
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
      domainId: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        changeDateFormat,
        isUserUS,
        noData: false,
        loading: true,
        totalItems: 0,
        customFiltersValues: this.activeFilterOnload,
        filters: [
          {
            source: "search",
            alwaysOn: true,
          },
        ],
        fields: [
          {
            source: "name",
            label: this.$t("resources.categories.fields.name"),
            sortable: true,
            width: "40%",
          },
          {
            source: "slug",
            label: this.$t("resources.categories.fields.slug"),
            sortable: true,
            width: "40%",
          },
          {
            source: "updatedAt",
            label: this.$t("resources.categories.fields.last_updated"),
            sortable: true,
            width: "20%",
          },
        ],
        bulkSelectedItems: [],
      };
    },
    methods: {
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      getBulkSelectedItems(val) {
        this.bulkSelectedItems = val;
      },
      async deleteResource(item) {
        this.$store.dispatch(`${this.resourceName}/displayDeleteModal`, {
          displayDeleteModal: true,
          items: [item],
          itemsList: this.$refs["list"].listState.items,
        });
      },
      duplicateResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_create`, query: { source: resourceId } });
      },
      editResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_edit`, params: { id: resourceId }, query: { domainId: this.domainId } });
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
    },
  };
</script>
